/**
 * CancelledLayoutChecker - Utility for checking cancelled layouts
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for checking if a layout has been cancelled
 * and handling cancelled layout scenarios.
 */

/**
 * Check if a layout is cancelled
 * 
 * @param cancelledLayoutIds - Set of cancelled layout IDs
 * @param layoutId - Layout ID to check
 * @returns True if the layout is cancelled
 */
export function isLayoutCancelled(
  cancelledLayoutIds: Set<number | string>,
  layoutId: number | string
): boolean {
  return cancelledLayoutIds.has(layoutId);
}

/**
 * Check if layout is cancelled and return early indicator
 * 
 * This is a convenience function for early return patterns.
 * 
 * @param cancelledLayoutIds - Set of cancelled layout IDs
 * @param layoutId - Layout ID to check
 * @param messageType - Type of message being checked (for logging)
 * @returns Object with cancellation status and shouldIgnore flag
 */
export function checkLayoutCancellation(
  cancelledLayoutIds: Set<number | string>,
  layoutId: number | string,
  messageType?: string
): {
  isCancelled: boolean;
  shouldIgnore: boolean;
} {
  const isCancelled = isLayoutCancelled(cancelledLayoutIds, layoutId);
  
  if (isCancelled && messageType) {
    console.log(`⏩ [Reactive] Ignoring ${messageType} for cancelled layout ${layoutId}`);
  }
  
  return {
    isCancelled,
    shouldIgnore: isCancelled
  };
}

/**
 * Mark a layout as cancelled
 * 
 * @param cancelledLayoutIds - Set of cancelled layout IDs
 * @param layoutId - Layout ID to mark as cancelled
 */
export function markLayoutAsCancelled(
  cancelledLayoutIds: Set<number | string>,
  layoutId: number | string
): void {
  cancelledLayoutIds.add(layoutId);
}

/**
 * Remove cancelled status from a layout
 * 
 * @param cancelledLayoutIds - Set of cancelled layout IDs
 * @param layoutId - Layout ID to remove from cancelled set
 * @returns True if the layout was in the cancelled set
 */
export function removeCancelledStatus(
  cancelledLayoutIds: Set<number | string>,
  layoutId: number | string
): boolean {
  return cancelledLayoutIds.delete(layoutId);
}

/**
 * Clear all cancelled layouts
 * 
 * @param cancelledLayoutIds - Set of cancelled layout IDs
 * @returns Number of layouts that were cleared
 */
export function clearAllCancelledLayouts(
  cancelledLayoutIds: Set<number | string>
): number {
  const count = cancelledLayoutIds.size;
  cancelledLayoutIds.clear();
  return count;
}


